// client/legend-cms/src/worldCupMatches/WorldCupMatchList.js
import React from 'react';
import {
  List,
  Datagrid,
  TextField,
  DateField,
  NumberField,
  EditButton,
  SelectField,
  ReferenceField,
  TopToolbar,
  CreateButton,
  TextInput,
  SelectInput,
  useNotify,
  useRefresh,
  useGetList,
} from 'react-admin';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField as MuiTextField,
  MenuItem,
} from '@material-ui/core';

import { makeStyles } from '@material-ui/core/styles';

const statusChoices = [
  { id: 'SCHEDULED', name: 'Agendado' },
  { id: 'OPEN', name: 'Aberto para palpites' },
  { id: 'CLOSED', name: 'Fechado para palpites' },
  { id: 'FINISHED', name: 'Finalizado' },
];

const useStyles = makeStyles({
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  actionButton: {
    height: 36,
    minWidth: 170,
    padding: '0 16px',
    fontSize: 13,
    fontWeight: 700,
    whiteSpace: 'nowrap',
  },
});

const filters = [
  <TextInput label="Buscar" source="q" alwaysOn />,
  <SelectInput
    label="Status"
    source="status"
    choices={statusChoices}
    alwaysOn
  />,
];

const getAuthHeaders = () => {
  const { access_token } = JSON.parse(
    localStorage.getItem('auth') || '{"access_token": ""}',
  );

  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${access_token}`,
  };
};

// Lista de times elegíveis (curada em Campanhas de Palpites > Times
// elegíveis para o Top 4) da campanha ativa. `null` enquanto carrega;
// array vazio significa "sem curadoria" (o modal cai no fallback de
// mostrar todos os times ativos), mesmo comportamento do app.
const useActiveCampaignTopFourTeamIds = () => {
  const [teamIds, setTeamIds] = React.useState(null);

  React.useEffect(() => {
    let isMounted = true;

    const fetchActiveCampaign = async () => {
      try {
        const filter = encodeURIComponent(JSON.stringify({ active: true }));
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/cms/prediction-campaigns?filter=${filter}&range=[0,0]&sort=["id","DESC"]`,
          { headers: getAuthHeaders() },
        );
        const json = await response.json();

        if (isMounted) {
          const campaign = Array.isArray(json) ? json[0] : null;
          setTeamIds(campaign?.top_four_team_ids || []);
        }
      } catch (error) {
        if (isMounted) setTeamIds([]);
      }
    };

    fetchActiveCampaign();

    return () => {
      isMounted = false;
    };
  }, []);

  return teamIds;
};

const FinishTopFourButton = () => {
  const notify = useNotify();
  const refresh = useRefresh();
  const classes = useStyles();

  // Times ativos cadastrados em prediction-teams alimentam o seletor do
  // Top 4. O modal envia o slug do time — o mesmo valor que o app envia no
  // palpite. Enviar o nome de exibição faria a comparação em
  // calculateTopFourXp não casar para qualquer time de nome composto, e
  // todos os palpites pontuariam 0 sem erro nenhum.
  const { data: teamsById, ids: teamIds } = useGetList(
    'prediction-teams',
    { page: 1, perPage: 500 },
    { field: 'name', order: 'ASC' },
    { active: true },
  );

  const eligibleTeamIds = useActiveCampaignTopFourTeamIds();

  const registeredTeams = (teamIds || [])
    .map((id) => teamsById?.[id])
    .filter(Boolean);

  // Se a campanha ativa curou uma lista de times para o Top 4, respeita
  // essa lista; senão (ou sem curadoria ainda), mostra todos os times
  // cadastrados ativos. Mesma regra do seletor do app.
  const teamChoices =
    eligibleTeamIds && eligibleTeamIds.length > 0
      ? registeredTeams.filter((team) => eligibleTeamIds.includes(team.id))
      : registeredTeams;

  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [topFourResult, setTopFourResult] = React.useState(null);

  const [firstPlaceTeam, setFirstPlaceTeam] = React.useState('');
  const [secondPlaceTeam, setSecondPlaceTeam] = React.useState('');
  const [thirdPlaceTeam, setThirdPlaceTeam] = React.useState('');
  const [fourthPlaceTeam, setFourthPlaceTeam] = React.useState('');

  const selectedTeams = [
    firstPlaceTeam,
    secondPlaceTeam,
    thirdPlaceTeam,
    fourthPlaceTeam,
  ];

  const hasFinishedTopFour = Boolean(topFourResult);

  const hasDuplicatedTeams = () => {
    const filledTeams = selectedTeams.filter(Boolean);
    return new Set(filledTeams).size !== filledTeams.length;
  };

  const isValid = () => {
    return selectedTeams.every(Boolean) && !hasDuplicatedTeams();
  };

  const resetForm = () => {
    setFirstPlaceTeam('');
    setSecondPlaceTeam('');
    setThirdPlaceTeam('');
    setFourthPlaceTeam('');
    setTopFourResult(null);
  };

  // Resultados finalizados antes da troca para slug guardaram o nome de
  // exibição; sem esse mapeamento o select apareceria vazio ao reabrir um
  // Top 4 já processado.
  const toTeamSlug = (value) => {
    if (!value) return '';

    const match = teamChoices.find(
      (team) => team.slug === value || team.name === value,
    );

    return match?.slug ?? value;
  };

  const fillFormWithResult = (result) => {
    setFirstPlaceTeam(toTeamSlug(result.first_place_team));
    setSecondPlaceTeam(toTeamSlug(result.second_place_team));
    setThirdPlaceTeam(toTeamSlug(result.third_place_team));
    setFourthPlaceTeam(toTeamSlug(result.fourth_place_team));
  };

  const fetchTopFourResult = async () => {
    const response = await fetch(
      `${process.env.REACT_APP_BASE_URL}/cms/world-cup/top-four-result`,
      {
        method: 'GET',
        headers: getAuthHeaders(),
      },
    );

    const json = await response.json();

    if (!response.ok) {
      throw new Error(json.message || 'Erro ao buscar Top 4 finalizado');
    }

    return json.data;
  };

  const handleClose = () => {
    if (!loading) {
      setOpen(false);
    }
  };

  const handleOpen = async () => {
    resetForm();
    setOpen(true);

    try {
      setLoading(true);

      const result = await fetchTopFourResult();

      if (result) {
        setTopFourResult(result);
        fillFormWithResult(result);
      }
    } catch (error) {
      notify(error.message || 'Erro ao buscar Top 4 finalizado', 'warning');
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = async () => {
    if (hasFinishedTopFour) {
      notify('O Top 4 já foi finalizado.', 'warning');
      return;
    }

    if (!isValid()) {
      notify(
        'Selecione quatro seleções diferentes para finalizar o Top 4.',
        'warning',
      );
      return;
    }

    const confirmed = window.confirm(
      'Tem certeza que deseja finalizar o Top 4? Essa ação vai processar XP dos palpites e não deve ser repetida.',
    );

    if (!confirmed) return;

    try {
      setLoading(true);

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/cms/world-cup/top-four/finish`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            first_place_team: firstPlaceTeam,
            second_place_team: secondPlaceTeam,
            third_place_team: thirdPlaceTeam,
            fourth_place_team: fourthPlaceTeam,
          }),
        },
      );

      const json = await response.json();

      if (!response.ok) {
        throw new Error(json.message || 'Erro ao finalizar Top 4');
      }

      notify(
        `Top 4 finalizado. Palpites processados: ${json.data?.processed_predictions ?? 0}`,
        'success',
      );

      setTopFourResult(json.data?.result || null);

      if (json.data?.result) {
        fillFormWithResult(json.data.result);
      }

      refresh();
    } catch (error) {
      notify(error.message || 'Erro ao finalizar Top 4', 'warning');
    } finally {
      setLoading(false);
    }
  };

  const renderTeamSelect = (label, value, onChange) => (
    <MuiTextField
      select
      label={label}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      margin="normal"
      fullWidth
      variant="outlined"
      disabled={loading || hasFinishedTopFour}
    >
      {teamChoices.map((team) => (
        <MenuItem key={team.id} value={team.slug}>
          {team.name}
        </MenuItem>
      ))}
    </MuiTextField>
  );

  return (
    <>
      <Button
        variant="contained"
        color="secondary"
        onClick={handleOpen}
        className={classes.actionButton}
      >
        Finalizar Top 4
      </Button>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>
          {hasFinishedTopFour
            ? 'Top 4 já finalizado'
            : 'Finalizar Top 4 da campanha ativa'}
        </DialogTitle>

        <DialogContent>
          {hasFinishedTopFour && (
            <p
              style={{
                marginTop: 0,
                marginBottom: 16,
                padding: '10px 12px',
                borderRadius: 8,
                backgroundColor: '#e8f5e9',
                color: '#2e7d32',
                fontWeight: 600,
              }}
            >
              O Top 4 já foi finalizado e a XP dos palpites já foi processada.
              Os times abaixo são o resultado oficial salvo no CMS.
            </p>
          )}

          {!hasFinishedTopFour && teamChoices.length === 0 && (
            <p
              style={{
                marginTop: 0,
                marginBottom: 16,
                padding: '10px 12px',
                borderRadius: 8,
                backgroundColor: '#fff8e1',
                color: '#5d4037',
                fontWeight: 600,
              }}
            >
              Nenhum time elegível para o Top 4 da campanha ativa. Cadastre
              times em "Times de Palpites" e revise a lista de times elegíveis
              da campanha.
            </p>
          )}

          {renderTeamSelect('Campeão', firstPlaceTeam, setFirstPlaceTeam)}
          {renderTeamSelect(
            'Vice-campeão',
            secondPlaceTeam,
            setSecondPlaceTeam,
          )}
          {renderTeamSelect('3º colocado', thirdPlaceTeam, setThirdPlaceTeam)}
          {renderTeamSelect('4º colocado', fourthPlaceTeam, setFourthPlaceTeam)}

          {!hasFinishedTopFour && hasDuplicatedTeams() && (
            <p style={{ color: '#c62828', marginTop: 8 }}>
              Não é permitido repetir times no Top 4.
            </p>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            {hasFinishedTopFour ? 'Fechar' : 'Cancelar'}
          </Button>

          {!hasFinishedTopFour && (
            <Button
              onClick={handleFinish}
              color="secondary"
              variant="contained"
              disabled={loading || !isValid()}
            >
              Confirmar e processar XP
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

const ListActions = () => {
  const classes = useStyles();

  return (
    <TopToolbar>
      <div className={classes.actions}>
        <FinishTopFourButton />

        <CreateButton
          basePath="/world-cup-matches"
          variant="contained"
          label="Novo jogo"
          className={classes.actionButton}
        />
      </div>
    </TopToolbar>
  );
};

const WorldCupMatchList = (props) => (
  <List
    {...props}
    filters={filters}
    actions={<ListActions />}
    bulkActionButtons={false}
    sort={{ field: 'match_date', order: 'ASC' }}
  >
    <Datagrid>
      <ReferenceField
        source="campaign_id"
        reference="prediction-campaigns"
        label="Campanha"
        link={false}
      >
        <TextField source="title" />
      </ReferenceField>
      <TextField source="title" label="Jogo" />
      <TextField source="home_team" label="Mandante" />
      <TextField source="away_team" label="Visitante" />
      <DateField source="match_date" label="Data do jogo" showTime />
      <DateField
        source="prediction_deadline"
        label="Limite palpites"
        showTime
      />
      <SelectField source="status" label="Status" choices={statusChoices} />
      <NumberField source="home_score" label="Gols mandante" />
      <NumberField source="away_score" label="Gols visitante" />
      <EditButton />
    </Datagrid>
  </List>
);

export default WorldCupMatchList;
